<template>
  <div class="camera-components">
    <div class="level">
      <h3 class="title is-5 mb-0">Installed components</h3>
      <div class="buttons">
        <button class="button is-light" :disabled="loading" @click="load(1)">
          Refresh</button
        ><button class="button is-primary" @click="openPicker">
          Install component
        </button>
      </div>
    </div>
    <div v-if="error" class="notification is-danger is-light" role="alert">
      {{ error }}
    </div>
    <p v-if="loading" role="status">Loading components...</p>
    <div v-else class="table-container">
      <table class="table is-fullwidth is-striped">
        <thead>
          <tr>
            <th>Type</th>
            <th>Identifier / label</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!history.current.length">
            <td colspan="3">No components are installed in this camera.</td>
          </tr>
          <tr v-for="row in history.current" :key="row.id">
            <td>{{ row.type }}</td>
            <td>
              <button
                class="button is-text is-small"
                @click="openComponentModal({ id: row.component_id })"
              >
                {{ row.identifier || row.label || `#${row.component_id}` }}
              </button>
            </td>
            <td>
              <button
                class="button is-danger is-light is-small"
                :disabled="busy"
                @click="
                  removing = row;
                  reason = 'unknown';
                  removalNote = '';
                "
              >
                Remove
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <form v-if="removing" class="box" @submit.prevent="remove">
      <h4 class="title is-6">
        Remove {{ removing.identifier || removing.label || removing.type }}?
      </h4>
      <p class="help mb-3">
        The installation stays in history. Faulty parts become faulty; other
        parts return to stock.
      </p>
      <label class="label" :for="`removal-reason-${inventoryId}`">Reason</label>
      <div class="select mb-3">
        <select :id="`removal-reason-${inventoryId}`" v-model="reason">
          <option
            v-for="value in ['faulty', 'upgrade', 'moved', 'unknown']"
            :key="value"
          >
            {{ value }}
          </option>
        </select>
      </div>
      <label class="label" :for="`removal-note-${inventoryId}`">Note</label
      ><textarea
        :id="`removal-note-${inventoryId}`"
        v-model="removalNote"
        class="textarea mb-3"
        maxlength="10000"
      />
      <div class="buttons">
        <button
          class="button is-danger"
          :class="{ 'is-loading': busy }"
          :disabled="busy"
        >
          Confirm removal</button
        ><button
          class="button"
          type="button"
          :disabled="busy"
          @click="removing = null"
        >
          Cancel
        </button>
      </div>
    </form>
    <form v-if="picker" class="box" @submit.prevent="install">
      <div class="level">
        <h4 class="title is-6 mb-0">Install an available component</h4>
        <button
          class="button is-small"
          type="button"
          @click="openComponentModal({ inventoryId })"
        >
          Create and install new
        </button>
      </div>
      <label class="label" :for="`part-search-${inventoryId}`"
        >Search stock components</label
      >
      <div class="field has-addons">
        <div class="control is-expanded">
          <input
            :id="`part-search-${inventoryId}`"
            v-model="search"
            class="input"
            maxlength="255"
            @input="searchParts"
          />
        </div>
        <div class="control">
          <button
            class="button"
            type="button"
            :disabled="partsLoading"
            @click="findParts(1)"
          >
            Search
          </button>
        </div>
      </div>
      <p v-if="partsLoading" role="status">Searching...</p>
      <p v-else-if="!parts.length">
        No available components match your search.
      </p>
      <label v-for="part in parts" :key="part.id" class="picker-option"
        ><input
          v-model="selected"
          type="radio"
          :name="`install-component-${inventoryId}`"
          :value="part.id"
        />
        {{ part.type }} - {{ part.identifier || part.label || `#${part.id}` }}
        <small>{{ part.manufacturer }} {{ part.model }}</small></label
      >
      <div v-if="partsLast > 1" class="buttons mt-3">
        <button
          class="button is-small"
          type="button"
          :disabled="partsPage <= 1 || partsLoading"
          @click="findParts(partsPage - 1)"
        >
          Previous</button
        ><span>Page {{ partsPage }} of {{ partsLast }}</span
        ><button
          class="button is-small"
          type="button"
          :disabled="partsPage >= partsLast || partsLoading"
          @click="findParts(partsPage + 1)"
        >
          Next
        </button>
      </div>
      <div class="field mt-3">
        <label class="label" :for="`install-note-${inventoryId}`"
          >Installation note</label
        ><textarea
          :id="`install-note-${inventoryId}`"
          v-model="installNote"
          class="textarea"
          maxlength="10000"
        />
      </div>
      <div class="buttons">
        <button
          class="button is-primary"
          :class="{ 'is-loading': busy }"
          :disabled="!selected || busy || partsLoading"
        >
          Confirm installation</button
        ><button
          class="button"
          type="button"
          :disabled="busy"
          @click="picker = false"
        >
          Cancel
        </button>
      </div>
    </form>
    <h3 class="title is-5 mt-5">Installation history</h3>
    <installation-history
      :history="history"
      :page="page"
      :loading="loading"
      @change-page="load"
    />
    <p class="help">
      To reinstall a removed part, use Install component. Only stock parts with
      no current installation are available.
    </p>
  </div>
</template>
<script>
import { mapState, mapActions } from "vuex";
import { getInventoryComponents, listComponents } from "@/api/components";
import InstallationHistory from "@/components/InstallationHistory.vue";
import toast from "@/utils/toast";
export default {
  name: "CameraComponents",
  components: { InstallationHistory },
  props: { inventoryId: { type: Number, required: true } },
  data: () => ({
    history: { current: [], data: [], last: 1 },
    page: 1,
    loading: false,
    error: "",
    busy: false,
    removing: null,
    reason: "unknown",
    removalNote: "",
    picker: false,
    search: "",
    selected: null,
    installNote: "",
    parts: [],
    partsPage: 1,
    partsLast: 1,
    partsLoading: false,
    searchTimer: null,
    searchVersion: 0,
  }),
  computed: mapState(["componentRevision"]),
  watch: {
    componentRevision() {
      this.load(this.page);
      if (this.picker) this.findParts(1);
    },
  },
  methods: {
    ...mapActions(["openComponentModal"]),
    async load(page = 1) {
      this.loading = true;
      this.error = "";
      try {
        this.history = await getInventoryComponents(this.inventoryId, {
          page,
          size: 10,
        });
        this.page = page;
      } catch (e) {
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },
    openPicker() {
      this.picker = true;
      this.selected = null;
      this.findParts(1);
    },
    searchParts() {
      clearTimeout(this.searchTimer);
      this.searchVersion += 1;
      this.selected = null;
      this.searchTimer = setTimeout(() => this.findParts(1), 250);
    },
    async findParts(page = 1) {
      const version = ++this.searchVersion;
      this.partsLoading = true;
      this.selected = null;
      try {
        const result = await listComponents({
          search: this.search,
          page,
          size: 10,
          available: true,
        });
        if (version !== this.searchVersion) return;
        this.parts = result.data;
        this.partsPage = page;
        this.partsLast = result.last;
      } catch (e) {
        if (version === this.searchVersion) this.error = e.message;
      } finally {
        if (version === this.searchVersion) this.partsLoading = false;
      }
    },
    async install() {
      if (this.busy || !this.selected) return;
      this.busy = true;
      this.error = "";
      try {
        await this.$store.dispatch("installComponent", {
          inventoryId: this.inventoryId,
          componentId: this.selected,
          note: this.installNote || null,
        });
        this.picker = false;
        this.installNote = "";
        toast.success("Component installed");
      } catch (e) {
        await Promise.all([this.load(this.page), this.findParts(1)]);
        this.error = e.message;
      } finally {
        this.busy = false;
      }
    },
    async remove() {
      if (this.busy) return;
      this.busy = true;
      this.error = "";
      try {
        await this.$store.dispatch("removeComponent", {
          inventoryId: this.inventoryId,
          installationId: this.removing.id,
          reason: this.reason,
          note: this.removalNote || null,
        });
        this.removing = null;
        toast.success("Component removed");
      } catch (e) {
        this.error = e.message;
      } finally {
        this.busy = false;
      }
    },
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.searchVersion += 1;
  },
};
</script>
<style scoped>
.picker-option {
  display: block;
  padding: 0.65rem;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  overflow-wrap: anywhere;
}
.picker-option input {
  margin-right: 0.5rem;
}
.camera-components {
  min-width: 0;
}
</style>
